import { useCallback } from 'react';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { companyStateJ, getCompanySyncIfResolved } from '../../../services/state/CompanyStateJ';
import { getForesightStore } from '../../../util/jotai-store';

/**
 * Returns an async function that resolves companies by id, fetching any that
 * are not yet cached. Reading `companyStateJ(id)` triggers `fetchCompanyRaw`
 * via its `atomWithDefault` default; awaiting the read resolves to the company.
 *
 * Ids that resolve to null (e.g. negative/placeholder ids) are filtered out, so
 * the returned array may be shorter than `companyIds`. A failed fetch rejects,
 * propagating to the caller.
 */
export function useGetCompanyData() {
  const store = getForesightStore();
  return useCallback(
    async (companyIds: number[]): Promise<ICompanyDataModel[]> => {
      const resolved = await Promise.all(companyIds.map((id) => store.get(companyStateJ(id))));
      return resolved.filter((c): c is ICompanyDataModel => c != null);
    },
    [store]
  );
}

/**
 * @deprecated For new PortfolioOverview-style code, prefer
 * `useCompanyMapLookup` from `services/hooks/useSaveCompanyData` (reactive,
 * map-backed) when a populated `companyMapAtom` is available on the page.
 *
 * Returns a function that synchronously reads a single previously-resolved
 * company from `companyStateJ`'s sync mirror. Will not trigger a fetch and
 * will not re-render the caller when the underlying data changes.
 */
export function useGetCompanyIfSet() {
  return useCallback((id: number): ICompanyDataModel | null => getCompanySyncIfResolved(id), []);
}
